<template>
  <div class="app-root">
    <button
      class="app-status-indicator"
      :class="statusClass"
      :title="statusTitle"
      type="button"
      aria-label="Application connection status"
    ></button>

    <router-view v-slot="{ Component }">
      <component :is="Component" />
    </router-view>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { isConnected } from './composables/useConnectivityStatus';

const recentlyUsedCache = ref(false);
let cacheIndicatorTimeout = null;

const clearCacheIndicatorTimeout = () => {
  if (cacheIndicatorTimeout) {
    clearTimeout(cacheIndicatorTimeout);
    cacheIndicatorTimeout = null;
  }
};

const showCachedIndicator = () => {
  recentlyUsedCache.value = true;
  clearCacheIndicatorTimeout();
  cacheIndicatorTimeout = setTimeout(() => {
    recentlyUsedCache.value = false;
    cacheIndicatorTimeout = null;
  }, 6000);
};

const handleOnline = () => {
  // no-op: shared connectivity monitor updates this state
};

const handleOffline = () => {
  // no-op: shared connectivity monitor updates this state
};

const handleApiCachedFallback = () => {
  showCachedIndicator();
};

const statusClass = computed(() => {
  if (!isConnected.value) {
    return 'is-offline';
  }

  if (recentlyUsedCache.value) {
    return 'is-cached';
  }

  return 'is-online';
});

const statusTitle = computed(() => {
  if (!isConnected.value) {
    return 'Offline mode';
  }

  if (recentlyUsedCache.value) {
    return 'Online (recently served from offline cache)';
  }

  return 'Online';
});

onMounted(() => {
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  window.addEventListener('app:api-cached-fallback', handleApiCachedFallback);
});

onBeforeUnmount(() => {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('app:api-cached-fallback', handleApiCachedFallback);
  clearCacheIndicatorTimeout();
});
</script>

<style scoped>
.app-root {
  position: relative;
}

.app-status-indicator {
  position: fixed;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 12px;
  border: 0;
  border-radius: 999px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8);
  opacity: 0.78;
  z-index: 999999999;
  pointer-events: none;
}

.app-status-indicator.is-online {
  background: #16a34a;
}

.app-status-indicator.is-offline {
  background: #dc2626;
}

.app-status-indicator.is-cached {
  background: #f59e0b;
}
</style>
